import React, { useContext, useEffect } from 'react';
import { useActor, useInterpret } from '@xstate/react';
import {
    actions,
    AnyEventObject,
    BaseActionObject,
    Interpreter,
    ResolveTypegenMeta,
    ServiceMap,
    TypegenDisabled,
} from 'xstate';
import keycloak, { REFRESH_TOKEN_INTERVAL } from '@/keycloak';
import authMachine, {
    KEYCLOAK_AUTH_SUCCESS,
    KEYCLOAK_AUTH_USER_INFO_SUCCESS,
    KEYCLOAK_AUTH_FAILURE,
    Context,
} from '@/machines/authMachine';
import localforage from 'localforage';
import { redirectToLogin } from '@/auth/redirectToLogin';

export const AUTH_STORAGE_KEY = 'bm-saui-auth';

export { REFRESH_TOKEN_INTERVAL };

type AuthService = Interpreter<
    Context,
    any,
    AnyEventObject,
    {
        value: any;
        context: Context;
    },
    ResolveTypegenMeta<TypegenDisabled, AnyEventObject, BaseActionObject, ServiceMap>
>;

export const AuthenticationContext = React.createContext({
    authService: null as null | AuthService,
    keycloak: null as null | typeof keycloak,
});

export const useAuthentication = () => useContext(AuthenticationContext);

const AuthenticationProvider: React.FC = ({ children }) => {
    const authService = useInterpret(authMachine);

    useEffect(() => {
        authService.start();
    }, []);

    useEffect(() => {
        localforage
            .getItem(AUTH_STORAGE_KEY)
            .then((tokens: { token: string; refreshToken: string } | null) =>
                initializeKeycloak(authService, tokens)
            );

        keycloak.onAuthRefreshSuccess = () => sendTokens(authService);

        keycloak.onAuthError = (error) => {
            authService.send({ type: KEYCLOAK_AUTH_FAILURE, payload: error });
        };

        keycloak.onTokenExpired = () => {
            keycloak
                .updateToken(REFRESH_TOKEN_INTERVAL)
                .catch(() => handleRefreshFailure(authService));
        };
    }, []);

    return (
        <AuthenticationContext.Provider value={{ authService, keycloak }}>
            {children}
        </AuthenticationContext.Provider>
    );
};

const initializeKeycloak = (
    authService: AuthService,
    tokens: { token: string; refreshToken: string } | null
) =>
    keycloak
        .init({
            onLoad: 'login-required',
            checkLoginIframe: false,
            token: tokens?.token,
            refreshToken: tokens?.refreshToken,
            responseMode: 'query',
        })
        .then((isAuthenticated: boolean) =>
            isAuthenticated
                ? handleInitAuthenticated(authService)
                : sendFailure(authService, 'login_throttled')
        );

const handleInitAuthenticated = (authService: AuthService) => {
    sendTokens(authService);
    keycloak.loadUserInfo().then((userInfo: any) => {
        authService.send({ type: KEYCLOAK_AUTH_USER_INFO_SUCCESS, payload: userInfo });
    });
};

const handleRefreshFailure = (authService: AuthService) => {
    const isRedirecting = redirectToLogin();
    if (!isRedirecting) sendFailure(authService, 'refresh_throttled');
};

const sendTokens = (authService: AuthService) =>
    authService.send({
        type: KEYCLOAK_AUTH_SUCCESS,
        payload: {
            token: keycloak.token,
            refreshToken: keycloak.refreshToken,
            sessionId: keycloak.sessionId,
        },
    });

const sendFailure = (authService: AuthService, reason: string) =>
    authService.send({ type: KEYCLOAK_AUTH_FAILURE, payload: reason });

export default AuthenticationProvider;
